import React from 'react';
import './DetailedItem.css';

type DisplayType = 'text' | 'tags' | 'list' | 'date' | 'custom';

interface IDetailedItem {
  label?: string;
  text?: string | number;
  helpertext?: string;
  type?: DisplayType;
  tags?: string[];
  list?: string[] | { label: string; value: string }[];
  customRender?: () => React.ReactNode;
  tagClassName?: string;
  listClassName?: string;
}

export const DetailedItem = (props: IDetailedItem) => {
  const {
    label,
    text,
    helpertext,
    type = 'text',
    tags = [],
    list = [],
    customRender,
    tagClassName = 'bg-gradient-to-r from-blue-50 via-white to-purple-50 text-gray-800 px-4 py-1.5 text-sm rounded-full border border-blue-100 shadow-sm hover:shadow-md transition-shadow',
    listClassName = 'space-y-2 text-gray-700',
  } = props;

  const [showAllTags, setShowAllTags] = React.useState(false);

  const renderContent = () => {
    switch (type) {
      case 'tags': {
        const maxVisibleTags = 3;
        const visibleTags = showAllTags ? tags : tags.slice(0, maxVisibleTags);
        const remainingCount = tags.length - maxVisibleTags;
        const remainingTags = tags.slice(maxVisibleTags);

        return (
          <div className='flex flex-wrap gap-2'>
            { tags.length > 0 ? (
              <>
                { visibleTags.map((tag, idx) => (
                  <span key={idx} className={tagClassName}>
                    { tag }
                  </span>
                )) }
                { !showAllTags && remainingCount > 0 && (
                  <span
                      className='bg-gray-200 text-gray-700 px-3 py-1 rounded-full cursor-pointer hover:bg-gray-300 transition-colors'
                      onClick={() => setShowAllTags(true)}
                      title={remainingTags.join(', ')}
                  >
                    +{ remainingCount }
                  </span>
                ) }
                { showAllTags && tags.length > maxVisibleTags && (
                  <span
                      className='bg-gray-200 text-gray-700 px-3 py-1 rounded-full cursor-pointer hover:bg-gray-300 transition-colors'
                      onClick={() => setShowAllTags(false)}
                  >
                    Show less
                  </span>
                ) }
              </>
            ) : (
              <p className='text-gray-500'>No tags</p>
            ) }
          </div>
        );
      }

      case 'list':
        return (
          <ul className={listClassName}>
            { list.length > 0 ? (
              list.map((item, idx) => (
                <li key={idx} className='flex items-start gap-2 text-sm'>
                  <span className='text-blue-500 mt-1'>•</span>
                  <span className='flex-1'>
                    { typeof item === 'string' ? item : item.label }
                  </span>
                </li>
              ))
            ) : (
              <li className='text-gray-500 text-sm'>—</li>
            ) }
          </ul>
        );

      case 'date': {
        const dateValue = text;
        let timestamp: Date;

        if (typeof dateValue === 'number') {
          timestamp =
            dateValue.toString().length === 10
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
        } else if (typeof dateValue === 'string' && dateValue) {
          timestamp = new Date(dateValue);
        } else {
          timestamp = new Date();
        }

        return (
          <p className='text-gray-700'>{ timestamp.toLocaleDateString() }</p>
        );
      }

      case 'custom':
        return customRender ? customRender() : null;

      case 'text':
      default:
        if (helpertext) {
          return (
            <span
                className='text-gray-600 text-sm cursor-help inline-block'
                title={helpertext}
            >
              { text }
            </span>
          );
        }
        return <p className='text-gray-600'>{ text }</p>;
    }
  };

  return (
    <div className='detailItem flex flex-col gap-1'>
      <p className='font-semibold text-xs text-gray-800'>{ label }</p>
      { renderContent() }
    </div>
  );
};
